import {
  Component,
  DestroyRef,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  ViewChildren
} from '@angular/core';
import { BaseListFiltersComponentComponent } from '@shared/components/base-list-filters-component/base-list-filters-component.component';
import { CHIPS_VIEW, ListService } from '@shared/modules/list/services/list.service';
import { Employee } from '@shared/models/employee.model';
import { Config } from '@shared/configs/config';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Contact } from '@modules/contacts/shared/models/contact.model';
import { PropertyType } from '@modules/projects/shared/enums/property-type.enum';
import { UserRoles } from '@shared/enums/user-roles.enum';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { MatDialogRef } from '@angular/material/dialog';
import { SharedModule } from '@shared/shared.module';
import { TranslateModule } from '@ngx-translate/core';
import { ListModule } from '@shared/modules/list/list.module';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { DateRangePickerV2Component } from '@shared/components/date-rangepicker-v2/date-rangepicker-v2.component';
import { ListEvent, ListEventType } from '@shared/modules/list/model/list-event.model';
// import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { ProjectStage } from '@shared/enums/project-stage.enum';

@Component({
  selector: 'projects-filters',
  templateUrl: './projects-filters.component.html',
  standalone: true,
  imports: [SharedModule, TranslateModule, ListModule, AngularSvgIconModule]
})
export class ProjectsFiltersComponent extends BaseListFiltersComponentComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  Config = Config;
  UserRoles = UserRoles;
  ButtonSize = ButtonSize;
  ButtonTypes = ButtonTypes;

  searchRoles: string;
  propertyTypes: any[] = [];
  protected stages = Object.entries(ProjectStage).map(([key, value]) => ({
    id: value,
    name: this.t.instant(`Projects.Stage.${value}`)
  }));

  @ViewChildren('daterangepicker') daterangepickers: DateRangePickerV2Component[];

  constructor(
    @Optional() @Inject(CHIPS_VIEW) public chipsView: boolean,
    public service: ListService,
    @Optional() private dialogRef: MatDialogRef<ProjectsFiltersComponent>
  ) {
    super(service);
  }

  ngOnInit(): void {
    this.createForm();
    this.setPropertyTypes();
    this.setSearchRoles();

    if (!this.chipsView) {
      this.service.readUrlParams(false);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  setSearchRoles() {
    const params = new URLSearchParams();
    const roles = [
      UserRoles.ROLE_EMPLOYEE_ADMIN.toString(),
      UserRoles.ROLE_EMPLOYEE_MANAGER.toString(),
      UserRoles.ROLE_EMPLOYEE_WORKER.toString()
    ].join(',');
    params.append('filters[a.role][in]', roles);

    this.searchRoles = params.toString();
  }

  createForm() {
    this.service.filtersForm = new UntypedFormGroup({
      '[responsibleEmployee-id][eq]': new UntypedFormControl(null),
      '[basicDataBox-propertyType][eq]': new UntypedFormControl(null),
      '[mainContact-id][in]': new UntypedFormControl(null),
      '[favourite][is not null]': new UntypedFormControl(null),
      '[deleted]': new UntypedFormControl(null),
      '[a-created][gte]': new UntypedFormControl(null),
      '[a-created][lte]': new UntypedFormControl(null),
      '[a-stage][in]': new UntypedFormControl(null)
    });
  }

  listenQueryParams(): void {
    this.service.eventEmitter
      .pipe(
        tap(({ type, data }: ListEvent<any>) => {
          if (type === ListEventType.QUERY_PARAMS) {
            const typeFilter = data['filters[mainContact.id][in]'];
            if (isNotNullOrUndefined(typeFilter) && !Array.isArray(typeFilter)) {
              const formattedArr = data['filters[mainContact.id][in]']?.split(',').map((id) => +id);
              data['filters[mainContact.id][in]'] = formattedArr;
            }

            this.setFiltersToForm(data);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  setPropertyTypes() {
    this.propertyTypes = Object.values(PropertyType).map((type) => {
      return {
        id: type,
        name: this.t.instant('Projects.PropertyType.' + type)
      };
    });
  }

  clearFilters() {
    Object.keys(this.service?.filtersForm.value).forEach((key) => {
      if (key !== '[a-stage][in]' && key !== '[favourite][is not null]') {
        this.service.filtersForm.get(key).setValue(null);
      }
    });

    this.daterangepickers?.forEach((picker) => picker.resetValue());
  }

  shouldShowClearFilters() {
    let show = false;
    Object.keys(this.service.filtersForm.value).forEach((key) => {
      if (
        this.service?.filtersForm?.value[key] &&
        key !== '[a-stage][in]' &&
        key !== '[favourite][is not null]'
      ) {
        show = true;
      }
    });
    return show;
  }

  creatorName(employee: Employee) {
    const e = new Employee(employee);
    return e.userPerson.previewName;
  }

  contactName(contact: Contact) {
    const c = new Contact(contact);
    return c.fullName;
  }

  responseTransform(res) {
    return res.records;
  }

  cancel() {
    this.dialogRef.close();
    this.refreshChips();
  }

  submit() {
    this.service.getRows();
    this.dialogRef.close();
    this.refreshChips();
  }
}
